import React, { useState, useRef, useEffect } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { query, onSnapshot, collection } from "firebase/firestore";
import { useFirestore } from "reactfire";

import {
  classNames,
  percentageToTailwind,
  percentageToTailwindTop,
  percentageToTailwindLeft,
  percentageToTailwindBottom,
} from "utils/utils";
import TabItem from "components/tabItem";
import { Tab } from "@headlessui/react";
import {
  Md2Soccer,
  Md2ArrowLeft,
  Md2Cards,
  Md2ChatOutline,
} from "react-icons-ng/md2";

import { GoArrowSwitch } from "react-icons-ng/go";

import LeagueTable from "components/leagueTable";
import {
  SCORES_CURRENT,
  NOT_STARTED,
  FIRST_HALF,
  FULL_TIME,
  IN_PLAY_IN_2ND_HALF,
  HALF_TIME,
  EVENT_GOAL,
  EVENT_OWN_GOAL,
  EVENT_PENALTY,
  EVENT_SUBSTITUTION_EVENT,
  EVENT_YELLOW_CARD_EVENT,
  EVENT_RED_CARD_EVENT,
} from "utils/constants";
import EventTimeStatus from "components/eventTime";
import { useTranslation } from "react-i18next";

const EventView = ({ event, closeModal }) => {
  const firestore = useFirestore();
  const [eventIsLoading, setEventIsLoading] = useState(false);
  const [eventStats, setEventStats] = useState([]);

  const [animate, setAnimate] = useState(false);

  const { t } = useTranslation();

  const handleModalClose = () => {
    closeModal();
  };

  useEffect(() => {
    if (!event) {
      return;
    }
    setEventIsLoading(true);

    let eventsQuery = query(
      collection(
        firestore,
        "leagues/" +
          event.leagueId +
          "/seasons/" +
          event.seasonId +
          "/rounds/" +
          event.roundId +
          "/events/" +
          event.eventId +
          "/statistics"
      )
    );

    const unsubscribe = onSnapshot(
      eventsQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setEventIsLoading(false);
          return;
        }

        /* const newScores = snapshot.docs[0].data()?.scores;
          if (newScores !== scores) {
            setScores(newScores);
  
            if (!initialLoad) {
              setAnimate(true);
            } else {
              setInitialLoad(false);
            }
          } */

        setEventStats(snapshot.docs[0].data());
        setEventIsLoading(false);
      },
      (error) => {
        console.log(error);
        setEventIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [event]);

  const eventGoals = eventStats?.events?.filter(
    (x) =>
      x.type_id === EVENT_GOAL ||
      x.type_id === EVENT_OWN_GOAL ||
      x.type_id === EVENT_PENALTY
  );

  const modalContent = (
    <div className="flex flex-col bg-[#EFF4F8]">
      <div className="sticky z-50 ml-2 -mt-10 top-2">
        <button
          type="button"
          onClick={handleModalClose}
          className="button text-textPrimary focus:outline-none p-2 bg-white shadow-lg rounded-full"
        >
          <ArrowLeftIcon className="h-6 w-6 text-black" />
        </button>
      </div>
      <EventHeader
        event={event}
        eventStats={eventStats}
        animate={animate}
        eventGoals={eventGoals}
      />
      <div className="flex flex-col items-center justify-center text-center pb-4 px-2">
        <Tab.Group>
          <Tab.List
            className={classNames(
              "flex space-x-1 rounded-xl  p-1 w-full bg-gray-300/30"
            )}
          >
            <TabItem
              title={t("statistics.tabs.teams")}
              //loading={isLoading}
              type={"tournament"}
            />
            <TabItem
              title={t("statistics.tabs.report")}
              //loading={isLoading}
              type={"tournament"}
            />
            <TabItem
              title={t("statistics.tabs.stats")}
              //loading={isLoading}
              type={"tournament"}
            />
            <TabItem
              title={t("statistics.tabs.table")}
              //loading={isLoading}
              type={"tournament"}
            />
          </Tab.List>

          <Tab.Panels className="mt-4 w-full">
            <Tab.Panel
              key="Lineups"
              className={classNames("flex space-y-3 w-full flex-col")}
            >
              {event?.teams &&
              eventStats?.formations &&
              eventStats?.lineups &&
              eventStats?.lineups?.length ? (
                <Formation
                  teams={event?.teams}
                  formations={eventStats?.formations}
                  lineups={eventStats?.lineups}
                  events={eventStats?.events}
                />
              ) : (
                "Lineups not available"
              )}
            </Tab.Panel>
            <Tab.Panel
              key="Report"
              className={classNames("flex space-y-3 w-full flex-col")}
            >
              <Comments
                comments={eventStats?.comments}
                eventGoals={eventGoals}
                teams={event?.teams}
              />
            </Tab.Panel>
            <Tab.Panel
              key="Stats"
              className={classNames("flex space-y-3 w-full flex-col")}
            >
              <Stats
                statistics={eventStats?.statistics}
                teams={event?.teams}
                state={event?.state_id}
              />
              {eventStats?.weatherreport && (
                <Weather weather={eventStats?.weatherreport} />
              )}
            </Tab.Panel>
            <Tab.Panel
              key="Table"
              className={classNames("flex space-y-3 w-full flex-col")}
            >
              <Table event={event} teams={event?.teams} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );

  const inputRef = useRef(null);
  return <IonContent className=" ion-padding ">{modalContent}</IonContent>;
};

export default EventView;

const Table = ({ event, teams }) => {
  return (
    <LeagueTable
      leagueId={event?.leagueId}
      seasonId={event?.seasonId}
      showLogos={false}
      highlightedTeams={[teams?.home?.id, teams?.away?.id]}
    />
  );
};

const Comments = ({ comments, eventGoals, teams }) => {
  const { t } = useTranslation();
  if (!comments.length)
    return <div className="text-black">{t("statistics.comments.empty")}</div>;

  const homeTeamId = teams?.home?.id;
  const homeTeamName = teams?.home?.name;
  const awayTeamId = teams?.away?.id;
  const awayTeamName = teams?.away?.name;

  let goalTicker = 0;

  const sortedEventGoals = eventGoals.sort((a, b) =>
    b.addition.localeCompare(a.addition)
  );

  const commentaries = comments
    .sort((a, b) => b.order - a.order)
    .map((comment, index) => {
      let minute = comment?.minute;

      if (comment?.minute_extra) {
        minute = minute + comment?.minute_extra;
      }

      const commentIsGoal = comment.is_goal;
      let goalInfo;
      let homeGoal = false;

      if (commentIsGoal) {
        if (
          /*   comment?.comment
            ?.toString()
            .includes(sortedEventGoals[goalTicker]?.player_name) && */
          comment?.minute === sortedEventGoals[goalTicker]?.minute
        ) {
          goalInfo = sortedEventGoals[goalTicker];
          if (goalInfo?.participant_id === homeTeamId) {
            homeGoal = true;
          }

          goalTicker++;
        }
      }

      let isYellowCard = comment?.comment
        ?.toLowerCase()
        .includes(`yellow card`);
      let isRedCard = comment?.comment?.toLowerCase().includes(`red card`);
      let isSubstitution = comment?.comment
        ?.toLowerCase()
        .includes(`substitution`);
      let isVarDecision = comment?.comment
        ?.toLowerCase()
        .includes(`var decision`);
      let firstHalfEnded = comment?.comment
        ?.toLowerCase()
        .includes(`first half ended`);
      let secondHalfEnded = comment?.comment
        ?.toLowerCase()
        .includes(`second half ended`);

      return (
        <div key={index}>
          {firstHalfEnded ? (
            <div className="flex items-center justify-center font-semibold text-xs py-2">
              {t("statistics.comments.half_time")}
            </div>
          ) : secondHalfEnded ? (
            <div className="flex items-center justify-center font-semibold text-xs py-2">
              {t("statistics.comments.full_time")}
            </div>
          ) : (
            <></>
          )}
          <div
            className={classNames(
              comment?.is_goal && goalInfo
                ? " bg-customGreen/30"
                : comment?.is_important
                ? "bg-white"
                : "bg-white",
              "flex rounded-sm py-3 text-sm pr-2"
            )}
          >
            <div className="flex w-12 items-center justify-center font-semibold">
              {minute}'
            </div>

            <div className="flex w-full text-left text-xs items-center">
              {comment?.is_goal && goalInfo ? (
                <div className="flex flex-col">
                  <span className="text-sm">
                    <span
                      className={classNames(
                        homeGoal && "font-semibold",
                        "pr-1"
                      )}
                    >
                      <span className="pr-1">{homeTeamName}</span>
                      <span>{goalInfo?.result?.split("-")[0]}</span>
                    </span>
                    -
                    <span
                      className={classNames(
                        !homeGoal && "font-semibold",
                        "pl-1"
                      )}
                    >
                      <span className="pr-1">
                        {goalInfo?.result?.split("-")[1]}
                      </span>
                      <span>{awayTeamName}</span>
                    </span>
                  </span>
                  <div className="flex py-2">
                    <div className="flex mr-3 justify-center items-center w-8 h-8">
                      <GoalIcon className="w-4  text-black rounded-full" />
                    </div>
                    <div className="flex flex-col justify-center">
                      <span>
                        <span className="font-semibold pr-1">
                          {t("statistics.comments.goal")}
                        </span>
                        <span className="">{goalInfo?.player_name}</span>
                      </span>
                      <span>
                        <span className="font-semibold pr-1">
                          {t("statistics.comments.assist")}
                        </span>
                        <span className="">
                          {goalInfo?.related_player_name
                            ? goalInfo?.related_player_name
                            : "-"}
                        </span>
                      </span>
                    </div>
                  </div>
                  <span>{comment?.comment}</span>
                </div>
              ) : (
                <div className="flex">
                  {isSubstitution ? (
                    <div className="flex mr-3 justify-center items-center w-8 shrink-0">
                      <GoArrowSwitch className="h-6 w-6" />
                    </div>
                  ) : comment?.is_important ? (
                    <div
                      className={classNames(
                        isYellowCard
                          ? "text-yellow-500 flex"
                          : isRedCard
                          ? "text-red-500 flex"
                          : "hidden",
                        " mr-3 justify-center items-center w-8 h-8 shrink-0"
                      )}
                    >
                      <Md2Cards className="h-6 w-6" />
                    </div>
                  ) : isVarDecision ? (
                    <div className="shrink-0 flex mr-3 justify-center items-center rounded-md px-1 py-0.5 border-red-500 border text-red-500">
                      VAR
                    </div>
                  ) : (
                    <></>
                  )}
                  <div className="flex flex-col justify-center">
                    {comment?.comment}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    });

  return <div className="flex flex-col space-y-2">{commentaries}</div>;
};

const Stats = ({ statistics, teams, state }) => {
  const { t } = useTranslation();

  if (!statistics) return <>{t("statistics.stats.no_stats")}</>;

  const homeTeamId = teams?.home?.id;
  const homeTeamName = teams?.home?.name;
  const awayTeamId = teams?.away?.id;
  const awayTeamName = teams?.away?.name;

  const statsGroup = statistics?.reduce(
    (entryMap, e) =>
      entryMap.set(e.type_id ? e.type_id : "No pick", [
        ...(entryMap.get(e.type_id) || []),
        e,
      ]),
    new Map()
  );

  const statsList = [...statsGroup]
    .sort((a, b) => a[0] - b[0])
    .filter((item) => item[0] !== 45 && item[0] !== 85 && item[0] !== 79)
    .map(([key, value], index) => {
      const homeValue = value.find((item) => item.participant_id === homeTeamId)
        ?.data?.value;
      const awayValue = value.find((item) => item.participant_id === awayTeamId)
        ?.data?.value;

      return (
        <StatsComponent
          index={index}
          homeStats={homeValue}
          awayStats={awayValue}
          label={key}
        />
      );
    });

  const posessionValueAwayData =
    statistics?.length &&
    statistics?.find(
      (x) => x.type_id === 45 && x.participant_id === awayTeamId
    );

  const posessionValueAway = posessionValueAwayData?.data?.value;

  return (
    <div className="flex flex-col bg-white rounded-md p-4 w-full space-y-2 text-sm text-secondary700">
      <div className="flex justify-center items-center w-full">
        <div className=" w-full grid items-center justify-start">
          <span className="truncate whitespace-nowrap uppercase font-bold tracking-wide text-sm text-pink-500">
            {homeTeamName}
          </span>
        </div>

        <div className="w-full grid items-center justify-end">
          <span className="truncate whitespace-nowrap uppercase font-bold tracking-wide text-sm text-black">
            {awayTeamName}
          </span>
        </div>
      </div>
      {state != NOT_STARTED && (
        <>
          <div className="flex text-xs items-center justify-center">
            {t("statistics.stats.ball_poss")}
          </div>

          <div className="flex justify-center items-center w-full">
            <div className="grow basis-0 justify-end flex pr-2">
              <div className=" w-full grid items-center justify-end text-xs text-textPrimary ">
                {posessionValueAway && 100 - posessionValueAway + "%"}
              </div>
            </div>
            <div className="flex flex-none">
              <PieChart
                primaryColor={"rgb(0 0 0)"}
                secondaryColor={"rgb(236 72 153)"}
                percentage={posessionValueAway}
              />
            </div>
            <div className="grow basis-0 flex pl-2">
              <div className="w-full grid items-center justify-start text-xs text-textPrimary ">
                {posessionValueAway && posessionValueAway + "%"}
              </div>
            </div>
          </div>
        </>
      )}

      {statsList}
    </div>
  );
};

const PieChart = ({ primaryColor, secondaryColor, percentage }) => {
  const backgroundImage = `conic-gradient(${primaryColor} 0% ${percentage}%, ${secondaryColor} ${percentage}%)`;

  return (
    <div
      style={{
        backgroundImage: backgroundImage,
      }}
      className="w-14 h-14 rounded-[50%]"
    ></div>
  );
};

const StatsComponent = ({ index, homeStats, awayStats, label }) => {
  const { t } = useTranslation();

  let labelList = {
    52: t("statistics.stats.goals"),
    34: t("statistics.stats.corners"),
    45: t("statistics.stats.ball_poss"),
    79: t("statistics.stats.assists"),
    83: t("statistics.stats.red_cards"),
    84: t("statistics.stats.yellow_cards"),
    85: t("statistics.stats.total_cards"),
    86: t("statistics.stats.shots"),
    87: t("statistics.stats.injuries"),
    88: t("statistics.stats.goals_conceeded"),
  };

  let sum = homeStats + awayStats;

  let homePercentage = percentageToTailwind(
    Math.round((homeStats / sum) * 100)
  );

  let awayPercentage = percentageToTailwind(
    Math.round((awayStats / sum) * 100)
  );

  return (
    <div className="flex flex-col space-y-2 h-full" key={index}>
      <div className="flex text-xs items-center justify-center">
        {labelList[label]}
      </div>
      <div className="mt-4 grid grid-cols-[auto_1fr_1fr_auto] space-x-1">
        <span className="text-xs text-textPrimary w-6">{homeStats}</span>
        <div className="flex justify-end bg-gray-200 h-3 rounded-l-md">
          <div
            className={classNames(
              homePercentage,
              "bg-pink-500",
              "shadow-sm rounded-l-md  flex text-xs "
            )}
          ></div>
        </div>

        <div className="flex bg-gray-200 h-3 rounded-r-md">
          <div
            className={classNames(
              awayPercentage,
              "bg-black",
              "shadow-sm rounded-r-md h-3 flex text-xs "
            )}
          ></div>
        </div>
        <span className="text-xs text-textPrimary w-6">{awayStats}</span>
      </div>
    </div>
  );
};

const Weather = ({ weather }) => {
  const { temperature, description, wind, current, feels_like, clouds, icon } =
    weather;

  return (
    <div className="mt-4 flex justify-center flex-col items-center text-sm">
      <img className="w-14 h-14" src={icon} alt="Weather Icon" />
      <span className="font-bold">{current?.temp} °C</span>
    </div>
    /*
      <div className="mt-4 flex justify-center flex-col items-center">
        <h3 className="text-lg font-medium">Weather</h3>
        <img className="w-14 h-14" src={icon} alt="Weather Icon" />
  
        <p>
          Temperature: {current?.temp}°C | Feels Like: {current?.feels_like}°C
        </p>
        <p>
          Clouds: {current?.clouds} | Humidity: {current?.humidity} | Pressure:{" "}
          {current?.pressure}
        </p>
      </div>
       <div className="">
        <div className="flex items-center justify-between mb-4 flex-col">
          <img className="w-14 h-14" src={icon} alt="Weather Icon" />
          <h2 className="text-xl font-bold">Weather Information</h2>
        </div>
        <div className="flex flex-col">
          <div className="mt-4">
            <h3 className="text-lg font-medium">Temperature</h3>
          </div>
          <div className="flex flex-col w-full justify-center items-center">
            <div className="flex">
              <span className="flex pr-2">
                <Md2WeatherSunsetUp className="h-6 w-6" />
              </span>
              <span>
                {temperature?.morning}°C ({feels_like?.morning}°C)
              </span>
            </div>
            <div className="flex">
              <span className="flex pr-2">
                <Md2WeatherSunny className="h-6 w-6" />
              </span>
              <span>
                {temperature?.day}°C ({feels_like?.day}°C)
              </span>
            </div>
            <div className="flex">
              <span className="flex pr-2">
                <Md2WeatherSunsetDown className="h-6 w-6" />
              </span>
              <span>
                {temperature?.evening}°C ({feels_like?.evening}°C)
              </span>
            </div>
            <div className="flex">
              <span className="flex pr-2">
                <Md2WeatherNight className="h-6 w-6" />
              </span>
              <span>
                {temperature?.night}°C ({feels_like?.night}°C)
              </span>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-lg font-medium">Description</h3>
          <p>{description}</p>
        </div>
        <div className="mt-4">
          <h3 className="text-lg font-medium">Wind</h3>
          <p>
            Speed: {wind?.speed} km/h | Direction: {wind?.direction}&deg;
          </p>
        </div>
        <div className="mt-4">
          <h3 className="text-lg font-medium">Current Weather</h3>
          <p>
            Temperature: {current?.temp}°C | Feels Like: {current?.feels_like}°C
          </p>
          <p>
            Clouds: {current?.clouds} | Humidity: {current?.humidity} | Pressure:{" "}
            {current?.pressure}
          </p>
        </div>
        <div className="mt-4">
          <h3 className="text-lg font-medium">Clouds</h3>
          <p>{clouds}</p>
        </div>
      </div> */
  );
};

const Formation = ({ teams, formations, lineups, events }) => {
  const { t, i18n } = useTranslation();

  const homeTeam = formations.find(
    (formation) => formation.location === "home"
  );
  const awayTeam = formations.find(
    (formation) => formation.location === "away"
  );

  const getVerticalClass = (rowIndex, formationFieldMap, home) => {
    const sizes = {
      4: [12, 35, 60, 85],
      5: [12, 30, 50, 70, 90],
    };

    const sizeArray = sizes[formationFieldMap.size];
    if (sizeArray) {
      const position = home ? "top-" : "bottom-";
      return home
        ? percentageToTailwindTop(sizeArray[rowIndex - 1])
        : percentageToTailwindBottom(sizeArray[rowIndex - 1]); //`[${sizeArray[rowIndex - 1]}%]`;
    }

    return "";
  };

  const getHorizontalClass = (rowIndex, colIndex, formationFieldMap) => {
    const rowCount = formationFieldMap.get(rowIndex);

    const positions = {
      1: [50],
      2: [40, 60],
      3: [25, 50, 75],
      4: [10, 37, 63, 90],
      5: [10, 30, 50, 70, 90],
    };

    const positionArray = positions[rowCount];
    if (positionArray) {
      return percentageToTailwindLeft(positionArray[colIndex - 1]);
      //"left-" + `[${positionArray[colIndex - 1]}]`;
    }

    return "";
  };

  const LineUps = ({ lineups, team, listView }) => {
    let home = teams?.home?.id === team;

    const formationFieldMap = new Map();

    lineups
      .filter((player) => player.team_id === team)
      .filter((player) => player.type_id === 11)
      .forEach((player) => {
        let rowIndex, colIndex;

        if (!player?.formation_field) {
          ({ rowIndex, colIndex } = getPositionIndexes(
            lineups,
            team,
            player,
            rowIndex,
            colIndex
          ));
        } else {
          const [numberBeforeColon, numberAfterColon] =
            player?.formation_field?.split(":");
          rowIndex = parseInt(numberBeforeColon);
          colIndex = parseInt(numberAfterColon);
        }

        if (formationFieldMap.has(rowIndex)) {
          const currentMax = formationFieldMap.get(rowIndex);
          if (colIndex > currentMax) {
            formationFieldMap.set(rowIndex, colIndex);
          }
        } else {
          formationFieldMap.set(rowIndex, colIndex);
        }
      });

    const players = lineups
      .filter((player) => player?.team_id === team)
      .filter((player) => player?.type_id === 11)
      .map((player) => {
        let rowIndex, colIndex;

        if (!player?.formation_field) {
          ({ rowIndex, colIndex } = getPositionIndexes(
            lineups,
            team,
            player,
            rowIndex,
            colIndex
          ));
        } else {
          const [row, col] = player?.formation_field.split(":");
          rowIndex = parseInt(row);
          colIndex = parseInt(col);
        }

        let verticalClass = getVerticalClass(rowIndex, formationFieldMap, home);

        let horizontalClass = getHorizontalClass(
          rowIndex,
          colIndex,
          formationFieldMap
        );

        const playerEvents = events
          .sort((a, b) => b.type_id - a.type_id)
          .filter(
            (event) =>
              event.player_id === player.player_id ||
              event.related_player_id === player.player_id
          );

        return listView ? (
          <div key={player?.id} className="flex justify-between">
            <div className="flex items-center">
              <span className="flex rounded-full bg-gray-200 w-6 h-6 items-center justify-center text-xs mr-2">
                {player?.jersey_number}
              </span>

              <span>{player?.player_name}</span>
            </div>
            <PlayerEvents
              playerEvents={playerEvents}
              player={player}
              sub={true}
            />
          </div>
        ) : (
          <div
            className={classNames(
              verticalClass,
              horizontalClass,
              "absolute flex"
            )}
            key={player?.id}
          >
            <div className="shadow-md flex text-center border border-white w-8 h-8 rounded-full  bg-white/80 text-black items-center justify-center absolute -translate-x-2/4 -translate-y-2/4">
              <span className="flex text-sm">{player?.jersey_number}</span>

              <span className="top-9  absolute text-center text-white text-xs">
                {player?.player_name}
              </span>
              <PlayerEvents playerEvents={playerEvents} player={player} />
            </div>
          </div>
        );
      });

    return players;
  };

  // Fallback for error in sportsmonks api where formation field is not set for some players
  const getPositionIndexes = (lineups, team, player, rowIndex, colIndex) => {
    const playerBefore = lineups
      .filter((item) => item.team_id === team)
      .find(
        (item) => item?.formation_position === player?.formation_position - 1
      );
    const playerAfter = lineups
      .filter((item) => item.team_id === team)
      .find(
        (item) => item?.formation_position === player?.formation_position + 1
      );

    if (playerBefore?.formation_field) {
      const [numberBeforeColonBefore, numberAfterColonBefore] =
        playerBefore?.formation_field?.split(":");

      rowIndex = parseInt(numberBeforeColonBefore);
      colIndex = parseInt(numberAfterColonBefore) + 1;
    } else if (playerAfter?.formation_field) {
      const [numberBeforeColonAfter, numberAfterColonAfter] =
        playerAfter?.formation_field?.split(":");

      rowIndex = parseInt(numberBeforeColonAfter);
      colIndex = parseInt(numberAfterColonAfter) - 1;
    }

    return { rowIndex, colIndex };
  };

  const Bench = ({ lineups, team }) => {
    const benchList = lineups
      .filter((player) => player.team_id === team)
      .filter((player) => player.type_id === 12)
      .map((player) => {
        const playerEvents = events
          .sort((a, b) => b.type_id - a.type_id)
          .filter(
            (event) =>
              event.player_id === player.player_id ||
              event.related_player_id === player.player_id
          );

        return (
          <div key={player?.id} className="flex justify-between">
            <div className="flex">
              <span className="flex rounded-full bg-gray-200 w-6 h-6 items-center justify-center text-xs mr-2">
                {player?.jersey_number}
              </span>

              <span className="flex">{player?.player_name}</span>
            </div>
            <PlayerEvents
              playerEvents={playerEvents}
              player={player}
              sub={true}
            />
          </div>
        );
      });

    return benchList;
  };

  return (
    <div className="flex flex-col w-full">
      <div
        style={{
          backgroundImage:
            "repeating-linear-gradient(rgb(6,174,86), rgb(6,174,86) 8.33333%, rgb(0,186,92) 8.33333%, rgb(0,186,92) 16.6667%)",
          //"repeating-linear-gradient(rgb(236, 72, 153), rgb(236, 72, 153) 8.33333%, rgb(244, 63, 94) 8.33333%, rgb(244, 63, 94) 16.6667%)",
        }}
        className="flex relative rounded-lg w-full h-[950px] justify-center items-center"
      >
        <div className="opacity-20 absolute mx-auto my-0 border-b-[3px] border-b-white border-x-[3px] border-x-white border-solid top-0 inset-x-0 h-[35px] w-[100px]"></div>

        <div className="opacity-20 absolute mx-auto my-0 border-b-[3px] border-b-white border-x-[3px] border-x-white border-solid top-0 inset-x-0 h-[100px] w-[60%]"></div>
        <div className="h-5 overflow-hidden absolute w-[86px] rotate-180 top-[100px]">
          <div className="h-10 opacity-20 w-20 rounded-[50%] border-[3px] border-solid border-white"></div>
        </div>
        <div className="opacity-20 absolute mx-auto my-0 border-t-[3px] border-t-white border-x-[3px] border-x-white border-solid bottom-0 inset-x-0 h-[35px] w-[100px]"></div>
        <div className="opacity-20 absolute mx-auto my-0 border-t-[3px] border-b-white border-x-[3px] border-x-white border-solid bottom-0 inset-x-0 h-[100px] w-[60%]"></div>
        <div className="h-5 overflow-hidden absolute w-[86px] bottom-[100px]">
          <div className="h-10 opacity-20 w-20 rounded-[50%] border-[3px] border-solid border-white"></div>
        </div>
        <div className="bg-white h-[3px] opacity-20 absolute w-full top-2/4"></div>
        <div className="h-[100px] opacity-20 w-[100px] rounded-[50%] border border-[#fff]"></div>
        <div className="absolute top-0 w-full h-1/2">
          <div className="absolute left-4 top-4 flex">
            <div className="text-xs text-white">
              <div className="">{teams?.home?.name}</div>

              <div className="">{homeTeam?.formation}</div>
            </div>
          </div>
          <LineUps lineups={lineups} team={teams?.home?.id} />
        </div>
        <div className="absolute bottom-0 w-full h-1/2">
          <div className="absolute left-4 top-4 flex">
            <div className="text-xs text-white">
              <div className="">{teams?.away?.name}</div>
              <div className="">{awayTeam?.formation}</div>
            </div>
          </div>

          <LineUps lineups={lineups} team={teams?.away?.id} />
        </div>
      </div>
      <div className="flex w-full flex-col ">
        <span className="uppercase text-sm font-semibold mt-4 mb-2">
          {teams?.home?.name}
        </span>
        <div className="flex flex-col bg-white rounded-md p-4 w-full space-y-2 text-sm text-secondary700">
          <span className="text-left p-1 uppercase">
            {t("statistics.teams.starting_lineup")}
          </span>

          <LineUps lineups={lineups} team={teams?.home?.id} listView={true} />
          <span className="text-left p-1 uppercase pt-4">
            {t("statistics.teams.bench")}
          </span>
          <Bench lineups={lineups} team={teams?.home?.id} />
        </div>
        <span className="uppercase text-sm font-semibold mt-4 mb-2">
          {teams?.away?.name}
        </span>
        <div className="flex flex-col bg-white rounded-md p-4 w-full space-y-2 text-sm text-secondary700">
          <span className="text-left p-1 uppercase">
            {t("statistics.teams.starting_lineup")}
          </span>

          <LineUps lineups={lineups} team={teams?.away?.id} listView={true} />
          <span className="text-left p-1 uppercase pt-4">
            {t("statistics.teams.bench")}
          </span>
          <Bench lineups={lineups} team={teams?.away?.id} />
        </div>
      </div>
    </div>
  );
};

const PlayerEvents = ({ playerEvents, player, sub }) => {
  const isDirectlyRelatedEvent = (event) =>
    event.player_id === player.player_id;
  const isIndirectlyRelatedEvent = (event) =>
    event.related_player_id === player.player_id;
  const Goals = () => {
    return (
      <span
        className={classNames(
          sub ? " flex pl-2" : "inline-block relative mr-0.5"
        )}
      >
        <Md2Soccer className="h-4 w-4 text-black bg-white rounded-full" />
      </span>
    );
  };
  const Card = ({ yellow }) => {
    return (
      <span
        className={classNames(sub ? " flex pl-2" : "absolute -top-7 -left-5")}
      >
        <div
          className={classNames(
            yellow ? "bg-yellow-500" : "bg-red-500",
            "w-3 h-4 text-xs justify-center items-center  flex text-white rounded-sm shadow-sm  -mb-1 -mr-0.5 row-start-1 col-start-1 leading-none z-10"
          )}
        ></div>
      </span>
    );
  };
  const Substitution = ({ time, subsituteOut }) => {
    return (
      <span
        className={classNames(
          sub ? "text-black pl-2" : "absolute -right-3 -top-7 text-white",
          "flex"
        )}
      >
        <Md2ArrowLeft
          className={classNames(
            subsituteOut ? "bg-orange-500" : "bg-customGreen rotate-180",
            "h-4 w-4   rounded-full text-white"
          )}
        />
        <span className="pl-1 text-xs ">{time}'</span>
      </span>
    );
  };

  return (
    <div
      className={classNames(
        sub
          ? "flex"
          : "min-w-full absolute right-[-17px] top-[20px] whitespace-nowrap w-full"
      )}
    >
      {playerEvents
        .sort((a, b) => b.type_id - a.type_id)
        .map((event, index) => {
          if (
            (event.type_id === EVENT_GOAL || event.type_id === EVENT_PENALTY) &&
            isDirectlyRelatedEvent(event)
          ) {
            return <Goals key={event.id} />;
          } else if (
            event.type_id === EVENT_RED_CARD_EVENT &&
            isDirectlyRelatedEvent(event)
          ) {
            return <Card key={event.id} yellow={false} />;
          } else if (
            event.type_id === EVENT_YELLOW_CARD_EVENT &&
            isDirectlyRelatedEvent(event)
          ) {
            return <Card key={event.id} yellow={true} />;
          } else if (
            event.type_id === EVENT_SUBSTITUTION_EVENT &&
            (isDirectlyRelatedEvent(event) || isIndirectlyRelatedEvent(event))
          ) {
            return (
              <Substitution
                key={event.id}
                time={event.minute}
                subsituteOut={isIndirectlyRelatedEvent(event)}
              />
            );
          }
          return null;
        })}
    </div>
  );
};

const EventHeader = ({ event, eventStats, animate, eventGoals }) => {
  const { image_path, name } = eventStats?.venue || {};
  const { state } = event || {};
  let settled = false;

  const homeTeamName = event?.teams?.home?.name;
  const awayTeamName = event?.teams?.away?.name;
  const homeTeamId = event?.teams?.home?.id;
  const awayTeamId = event?.teams?.away?.id;

  const homeTeamScore = event?.scores?.find(
    (x) => x.type_id === SCORES_CURRENT && x.participant_id === homeTeamId
  )?.score?.goals;
  const awayTeamScore = event?.scores?.find(
    (x) => x.type_id === SCORES_CURRENT && x.participant_id === awayTeamId
  )?.score?.goals;

  const backgroundImage = image_path
    ? image_path
    : "https://firebasestorage.googleapis.com/v0/b/oles-test-project.appspot.com/o/common%2Fstadium.png?alt=media&token=7075d1e6-b2d6-48e1-ae5e-5f12d570d847";

  return (
    <div
      style={{ "--image-url": `url(${backgroundImage})` }}
      className="bg-[image:var(--image-url)] w-full h-full mb-4 bg-no-repeat bg-center bg-cover flex"
    >
      <div className="bg-gradient-to-b  from-transparent to-black/60 pt-14 w-full h-full items-center justify-between flex flex-col ">
        <div className="flex flex-col h-full justify-center items-center w-full">
          <EventTeamNames
            homeName={homeTeamName}
            awayName={awayTeamName}
            homeScore={homeTeamScore}
            awayScore={awayTeamScore}
            state={state}
            animate={animate}
          />
          <div className="flex flex-row px-2">
            <div className="flex w-fit  font-medium text-sm -skew-x-[10deg] text-white p-1 px-2 bg-pink-500 h-6 items-center justify-center">
              <div className=" skew-x-[10deg]">
                <div className=" w-full grid">
                  <span className="truncate whitespace-nowrap">
                    {name ? name : "-"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-fit  text-sm -skew-x-[10deg]  p-1 px-2 bg-white h-6 items-center justify-center">
              <div className=" skew-x-[10deg]  font-medium text-sm">
                <EventTimeStatus
                  startTime={
                    event?.startTime?.seconds
                      ? event?.startTime?.seconds
                      : event?.startTime?._seconds ?? null
                  }
                  eventState={event?.state_id}
                  eventPeriods={event?.periods}
                  vertical={false}
                  lastUpdatedAt={event?.statsUpdatedAt?.seconds}
                />
              </div>
            </div>
          </div>
          <EventScores goals={eventGoals} teams={event?.teams} />
        </div>
      </div>
    </div>
  );
};

const EventTeamNames = ({
  homeName,
  awayName,
  homeScore,
  awayScore,
  state,
  animate,
}) => {
  let centered = false;

  return (
    <div className="flex justify-center items-center w-full shadow-xs px-2 ">
      <div
        className={classNames(
          centered ? "" : "grow basis-0 justify-end",
          "flex"
        )}
      >
        <div className=" w-fit -skew-x-[10deg] p-1 px-4 bg-black z-20  grid items-center justify-end">
          <span className="truncate whitespace-nowrap uppercase font-bold tracking-wide text-sm text-white">
            {homeName ? homeName : "-"}
          </span>
        </div>
      </div>

      <div className="flex flex-none bg-black -skew-x-[10deg] h-7 text-lg font-semibold leading-4">
        {state == NOT_STARTED ? (
          <span className="flex py-1 bg-white text-black px-1  items-center justify-center">
            VS
          </span>
        ) : (
          <>
            <span
              className={classNames(
                animate ? "bg-customGreen animate-pulse text-black" : "",
                "flex py-1 bg-white w-5 text-black px-1 mr-0.5  items-center justify-center"
              )}
            >
              {homeScore !== undefined ? homeScore : "-"}
            </span>
            <span className="flex py-1 bg-white w-5 text-black px-1 ml-0.5  items-center justify-center">
              {awayScore !== undefined ? awayScore : "-"}
            </span>
          </>
        )}
      </div>

      <div className={classNames(centered ? "" : "grow basis-0", "flex")}>
        <div className=" w-fit -skew-x-[10deg] p-1 bg-black z-20  grid px-4 items-center justify-end">
          <span className="truncate whitespace-nowrap uppercase font-bold tracking-wide text-sm text-white">
            {awayName ? awayName : "-"}
          </span>
        </div>
      </div>
    </div>
  );
};

const EventScores = ({ goals, teams }) => {
  // for each event goal, return a row with the info

  if (!goals) return null;
  const homeTeamId = teams?.home?.id;

  let goalsList = goals
    .sort((a, b) => a.minute - b.minute)
    .map((goal) => {
      const isHome = goal.participant_id === homeTeamId;
      return (
        <div
          key={goals?.id}
          className="flex justify-center items-center w-full text-white text-xs"
        >
          <div className="grow basis-0 justify-end flex">
            <div className="w-fit grid ">
              <span className="truncate whitespace-nowrap ">
                {goal?.player_name && isHome && goal?.player_name}{" "}
                {goal.type_id === EVENT_PENALTY && isHome && "(P)"}
                {goal.type_id === EVENT_OWN_GOAL && isHome && "(OG)"}
              </span>
            </div>

            <span className="pl-1 font-extralight">
              {goal?.minute && isHome && goal?.minute + "'"}
            </span>
          </div>

          <div className="flex flex-none px-2 font-semibold  items-center justify-center ">
            {goal?.result || "-"}
          </div>

          <div className="grow basis-0 justify-start flex">
            <div className="w-fit grid ">
              <span className="truncate whitespace-nowrap ">
                {goal?.player_name && !isHome && goal?.player_name}{" "}
                {goal.type_id === EVENT_PENALTY && !isHome && "(P)"}
                {goal.type_id === EVENT_OWN_GOAL && !isHome && "(OG)"}
              </span>
            </div>
            <span className="pl-1 font-extralight">
              {goal?.minute && !isHome && goal?.minute + "'"}
            </span>
          </div>
        </div>
      );
    });
  return (
    <div className="flex flex-col space-y-1 justify-center items-center w-full  z-10 bg-transparent py-4 px-2 bg-gradient-to-b  from-transparent to-black/70 rounded-b-lg">
      {goalsList}
    </div>
  );
};

const GoalIcon = () => {
  return (
    <svg
      enable-background="new 0 0 64 64"
      height="64"
      viewBox="0 0 64 64"
      width="64"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="m26.55 40.18c-2.21-2.609-3.54-5.989-3.54-9.68 0-2.92.841-5.65 2.29-7.96h.011l6.29-.92 2.8-5.66v-.02c1.149-.29 2.359-.44 3.6-.44h.01c1.24 0 2.45.15 3.61.44l-.01.02 2.8 5.66 6.29.92.01-.01c1.261 2 2.061 4.31 2.25 6.8h-.01l-4.59 4.47 1.1 6.38c-.09.11-.189.21-.279.32-.37.42-.761.81-1.181 1.18-1.24 1.11-2.67 2.021-4.229 2.67l-5.762-3.03-5.76 3.029c-1.569-.649-3.01-1.56-4.25-2.68-.41-.36-.8-.75-1.17-1.17-.09-.109-.189-.209-.28-.319zm15.35-4.68 2.41-7.41-6.31-4.59-6.31 4.59 2.41 7.41z"
        fill="#e6e9ed"
      />
      <path
        d="m63 13.5v45h-5v-8-10-10-10-7c0-1.66-1.34-3-3-3h-7-10-10-10-10-7v-5h54c4.42 0 8 3.58 8 8z"
        fill="#e6e9ed"
      />
      <g fill="#656d78">
        <path d="m44.311 28.09-2.411 7.41h-7.799l-2.411-7.41 6.31-4.59z" />
        <path d="m26.83 20.5c.37-.42.76-.81 1.17-1.17 1.79-1.61 3.98-2.79 6.4-3.39v.02l-2.8 5.66-6.29.92h-.01c.45-.72.96-1.41 1.53-2.04z" />
        <path d="m48 19.32c.42.37.82.77 1.181 1.189.569.63 1.079 1.3 1.529 2.021l-.01.01-6.29-.92-2.8-5.66.01-.02c2.41.59 4.59 1.77 6.38 3.38z" />
        <path d="m48.36 33.8 4.59-4.47h.01c.03.38.05.78.05 1.17 0 3.69-1.329 7.07-3.55 9.68z" />
        <path d="m27.659 33.8-4.59-4.47h-.01c-.03.38-.05.78-.05 1.17 0 3.69 1.329 7.07 3.55 9.68z" />
        <path d="m38.01 41.32 5.761 3.029c-1.78.74-3.721 1.15-5.761 1.15h-.01c-2.04 0-3.979-.41-5.75-1.15z" />
      </g>
      <path d="m63 14.501c-.553 0-1-.448-1-1 0-3.86-3.141-7-7-7-.553 0-1-.448-1-1s.447-1 1-1c4.963 0 9 4.038 9 9 0 .552-.447 1-1 1z" />
      <path d="m58 14.501c-.553 0-1-.448-1-1 0-1.103-.897-2-2-2-.553 0-1-.448-1-1s.447-1 1-1c2.206 0 4 1.794 4 4 0 .552-.447 1-1 1z" />
      <path d="m63 59.499c-.553 0-1-.447-1-1v-44.998c0-.552.447-1 1-1s1 .448 1 1v44.998c0 .553-.447 1-1 1z" />
      <path d="m58 59.499c-.553 0-1-.447-1-1v-44.998c0-.552.447-1 1-1s1 .448 1 1v44.998c0 .553-.447 1-1 1z" />
      <path d="m55 11.501h-54c-.553 0-1-.448-1-1s.447-1 1-1h54c.553 0 1 .448 1 1s-.447 1-1 1z" />
      <path d="m55 6.501h-54c-.553 0-1-.448-1-1s.447-1 1-1h54c.553 0 1 .448 1 1s-.447 1-1 1z" />
      <path d="m48 59.499c-.553 0-1-.447-1-1v-16.816c0-.553.447-1 1-1s1 .447 1 1v16.816c0 .553-.447 1-1 1z" />
      <path d="m48 20.315c-.553 0-1-.448-1-1v-8.813c0-.552.447-1 1-1s1 .448 1 1v8.813c0 .553-.447 1-1 1z" />
      <path d="m38 59.499c-.553 0-1-.447-1-1v-13.001c0-.553.447-1 1-1s1 .447 1 1v13.001c0 .553-.447 1-1 1z" />
      <path d="m38 16.501c-.553 0-1-.448-1-1v-4.998c0-.552.447-1 1-1s1 .448 1 1v4.998c0 .552-.447 1-1 1z" />
      <path d="m28 59.499c-.553 0-1-.447-1-1v-16.826c0-.553.447-1 1-1s1 .447 1 1v16.826c0 .553-.447 1-1 1z" />
      <path d="m28 20.325c-.553 0-1-.448-1-1v-8.822c0-.552.447-1 1-1s1 .448 1 1v8.822c0 .552-.447 1-1 1z" />
      <path d="m18 59.499c-.553 0-1-.447-1-1v-47.996c0-.552.447-1 1-1s1 .448 1 1v47.996c0 .553-.447 1-1 1z" />
      <path d="m8 59.499c-.553 0-1-.447-1-1v-47.996c0-.552.447-1 1-1s1 .448 1 1v47.996c0 .553-.447 1-1 1z" />
      <path d="m26.824 21.501h-25.824c-.553 0-1-.448-1-1s.447-1 1-1h25.824c.553 0 1 .448 1 1s-.447 1-1 1z" />
      <path d="m58 21.501h-8.814c-.553 0-1-.448-1-1s.447-1 1-1h8.814c.553 0 1 .448 1 1s-.447 1-1 1z" />
      <path d="m23.006 31.502h-22.006c-.553 0-1-.448-1-1s.447-1 1-1h22.006c.553 0 1 .448 1 1s-.447 1-1 1z" />
      <path d="m58 31.502h-4.994c-.553 0-1-.448-1-1s.447-1 1-1h4.994c.553 0 1 .448 1 1s-.447 1-1 1z" />
      <path d="m26.828 41.501h-25.828c-.553 0-1-.447-1-1s.447-1 1-1h25.828c.553 0 1 .447 1 1s-.447 1-1 1z" />
      <path d="m58 41.501h-8.818c-.553 0-1-.447-1-1s.447-1 1-1h8.818c.553 0 1 .447 1 1s-.447 1-1 1z" />
      <path d="m58 51.501h-57c-.553 0-1-.447-1-1s.447-1 1-1h57c.553 0 1 .447 1 1s-.447 1-1 1z" />
      <path d="m38.006 46.498c-8.822 0-16-7.177-16-15.999 0-8.821 7.178-15.998 16-15.998s16 7.177 16 15.998c0 8.822-7.178 15.999-16 15.999zm0-29.997c-7.72 0-14 6.279-14 13.998s6.28 13.999 14 13.999 14-6.28 14-13.999-6.28-13.998-14-13.998z" />
      <path d="m50.7 23.536c-.048 0-.097-.003-.146-.01l-6.287-.916c-.325-.047-.606-.252-.752-.547l-2.799-5.664c-.245-.495-.042-1.095.453-1.339.498-.245 1.097-.042 1.34.454l2.566 5.193 5.768.84c.546.08.925.587.845 1.134-.072.497-.5.855-.988.855z" />
      <path d="m49.456 41.175c-.479 0-.901-.344-.984-.831l-1.096-6.38c-.056-.324.052-.655.288-.886l4.584-4.463c.395-.387 1.027-.378 1.414.019.385.396.377 1.029-.02 1.414l-4.207 4.097 1.007 5.861c.094.544-.271 1.061-.816 1.154-.057.01-.114.015-.17.015z" />
      <path d="m32.247 45.348c-.359 0-.707-.194-.887-.535-.257-.488-.068-1.094.421-1.351l5.76-3.025c.291-.152.639-.153.93.001l5.758 3.025c.489.257.678.861.42 1.35-.256.489-.858.68-1.35.42l-5.293-2.78-5.295 2.781c-.148.077-.308.114-.464.114z" />
      <path d="m26.554 41.175c-.056 0-.113-.005-.17-.015-.545-.094-.91-.61-.816-1.154l1.007-5.861-4.207-4.097c-.396-.385-.404-1.018-.02-1.414.386-.396 1.019-.405 1.414-.019l4.584 4.463c.236.23.344.562.288.886l-1.096 6.38c-.083.487-.506.831-.984.831z" />
      <path d="m25.31 23.536c-.489 0-.916-.358-.988-.856-.08-.546.299-1.054.845-1.134l5.77-.84 2.566-5.193c.243-.495.844-.697 1.34-.454.495.245.698.844.453 1.339l-2.799 5.664c-.146.295-.427.5-.752.547l-6.289.916c-.05.008-.099.011-.146.011z" />
      <path d="m41.898 36.503h-7.798c-.433 0-.817-.278-.951-.69l-2.412-7.414c-.134-.412.013-.864.363-1.119l6.313-4.586c.352-.255.824-.255 1.176 0l6.311 4.586c.351.255.497.707.363 1.119l-2.412 7.414c-.135.412-.52.69-.953.69zm-7.072-2h6.346l1.963-6.032-5.135-3.732-5.137 3.731z" />
    </svg>
  );
};
